import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_BORDER_COLOR } from '../constants/print';
import {
  SelectionContext,
  type CardEntityByVariant,
  type CardVariant,
  type SelectedCard,
} from './selectionContextDef';

const BORDER_COLOR_STORAGE_PREFIX = 'mythic-card-forge:border-colors';
const GLOBAL_BORDER_COLOR_STORAGE_KEY = `${BORDER_COLOR_STORAGE_PREFIX}:global`;
const CARD_BORDER_COLOR_STORAGE_KEY = `${BORDER_COLOR_STORAGE_PREFIX}:cards`;
const SELECTED_CARDS_STORAGE_KEY = 'mythic-card-forge:selected-cards';

const CARD_VARIANTS: CardVariant[] = ['spells', 'items', 'classes', 'feats'];

function getSelectionKey(variant: CardVariant, entityId: string): string {
  return `${variant}:${entityId}`;
}

function loadPersistedGlobalBorderColor(fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  return window.localStorage.getItem(GLOBAL_BORDER_COLOR_STORAGE_KEY) || fallback;
}

function loadPersistedCardBorderColors(): Map<string, string> {
  if (typeof window === 'undefined') return new Map();

  const stored = window.localStorage.getItem(CARD_BORDER_COLOR_STORAGE_KEY);
  if (!stored) return new Map();

  try {
    const parsed = JSON.parse(stored) as Record<string, string>;
    return new Map(Object.entries(parsed));
  } catch {
    return new Map();
  }
}

function loadPersistedSelectedCards(): SelectedCard[] {
  if (typeof window === 'undefined') return [];

  const stored = window.localStorage.getItem(SELECTED_CARDS_STORAGE_KEY);
  if (!stored) return [];

  try {
    const parsed = JSON.parse(stored) as unknown;
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((card): card is SelectedCard =>
      card !== null &&
      typeof card === 'object' &&
      typeof (card as SelectedCard).key === 'string' &&
      CARD_VARIANTS.includes((card as SelectedCard).variant) &&
      (card as SelectedCard).entity !== null &&
      typeof (card as SelectedCard).entity === 'object'
    );
  } catch {
    return [];
  }
}

export function SelectionProvider({ children }: { children: ReactNode }) {
  const [selectedCards, setSelectedCards] = useState<SelectedCard[]>(() =>
    loadPersistedSelectedCards()
  );
  const [globalBorderColor, setGlobalBorderColor] = useState(() =>
    loadPersistedGlobalBorderColor(DEFAULT_BORDER_COLOR)
  );
  const [cardBorderColors, setCardBorderColors] = useState<Map<string, string>>(() =>
    loadPersistedCardBorderColors()
  );

  const selectedKeySet = useMemo(() => {
    return new Set(selectedCards.map((card) => card.key));
  }, [selectedCards]);

  useEffect(() => {
    window.localStorage.setItem(GLOBAL_BORDER_COLOR_STORAGE_KEY, globalBorderColor);
  }, [globalBorderColor]);

  useEffect(() => {
    window.localStorage.setItem(SELECTED_CARDS_STORAGE_KEY, JSON.stringify(selectedCards));
  }, [selectedCards]);

  useEffect(() => {
    window.localStorage.setItem(
      CARD_BORDER_COLOR_STORAGE_KEY,
      JSON.stringify(Object.fromEntries(cardBorderColors))
    );
  }, [cardBorderColors]);

  const isSelected = useCallback(
    (variant: CardVariant, entityId: string) => selectedKeySet.has(getSelectionKey(variant, entityId)),
    [selectedKeySet]
  );

  const ensureColorForKey = useCallback((selectionKey: string) => {
    setCardBorderColors((prev) => {
      if (prev.has(selectionKey)) return prev;
      const updated = new Map(prev);
      updated.set(selectionKey, globalBorderColor);
      return updated;
    });
  }, [globalBorderColor]);

  const toggleCard = useCallback(<V extends CardVariant>(variant: V, entity: CardEntityByVariant[V]) => {
    const selectionKey = getSelectionKey(variant, entity.id);

    setSelectedCards((prev) => {
      const existingIndex = prev.findIndex((card) => card.key === selectionKey);
      if (existingIndex >= 0) {
        return prev.filter((card) => card.key !== selectionKey);
      }

      const card = { key: selectionKey, variant, entity } as SelectedCard;
      return [...prev, card];
    });

    ensureColorForKey(selectionKey);
  }, [ensureColorForKey]);

  const addCards = useCallback(<V extends CardVariant>(variant: V, entities: CardEntityByVariant[V][]) => {
    if (entities.length === 0) return;

    setSelectedCards((prev) => {
      const existingKeys = new Set(prev.map((card) => card.key));
      const additions: SelectedCard[] = [];

      for (const entity of entities) {
        const selectionKey = getSelectionKey(variant, entity.id);
        if (!existingKeys.has(selectionKey)) {
          existingKeys.add(selectionKey);
          additions.push({ key: selectionKey, variant, entity } as SelectedCard);
        }
      }

      if (additions.length === 0) return prev;
      return [...prev, ...additions];
    });

    setCardBorderColors((prev) => {
      const updated = new Map(prev);
      for (const entity of entities) {
        const selectionKey = getSelectionKey(variant, entity.id);
        if (!updated.has(selectionKey)) {
          updated.set(selectionKey, globalBorderColor);
        }
      }
      return updated;
    });
  }, [globalBorderColor]);

  const clearAllCards = useCallback(() => {
    setSelectedCards([]);
  }, []);

  const getCardBorderColor = useCallback((variant: CardVariant, entityId: string): string | undefined => {
    return cardBorderColors.get(getSelectionKey(variant, entityId));
  }, [cardBorderColors]);

  const setCardBorderColor = useCallback((variant: CardVariant, entityId: string, color: string) => {
    setCardBorderColors((prev) => {
      const updated = new Map(prev);
      updated.set(getSelectionKey(variant, entityId), color);
      return updated;
    });
  }, []);

  const applyBorderColorToAll = useCallback(() => {
    setCardBorderColors((prev) => {
      const updated = new Map(prev);
      selectedCards.forEach((card) => {
        updated.set(card.key, globalBorderColor);
      });
      return updated;
    });
  }, [globalBorderColor, selectedCards]);

  return (
    <SelectionContext.Provider
      value={{
        selectedCards,
        selectedCount: selectedCards.length,
        globalBorderColor,
        setGlobalBorderColor,
        isSelected,
        toggleCard,
        addCards,
        clearAllCards,
        getCardBorderColor,
        setCardBorderColor,
        applyBorderColorToAll,
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
}
